// import components
import updateExerciseStatus from 'actions/authentication/updateExerciseStatus';
import showHomework from 'actions/homework/show';
import BackButton from 'components/Layout/BackButton/BackButton';
import Background from 'components/Layout/Background/Background';
import Menu from 'components/Layout/Menu/Menu';
import { AnimatePresence } from 'framer-motion';
import React, { createContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { getExercisesByHomework } from 'selectors/exercises/by-homework';
import ConditionalExercise from './components/ConditionalExercise';
import NavigationSequence from './components/NavigationSequence';
import './Homework.module.scss';

export type SequenceOptions =
    | 'terugblik'
    | 'doel'
    | 'voorkennis'
    | 'instructie'
    | 'oefening'
    | 'succescriteria'
    | 'afgerond';

type HomeworkContextTypes = {
    homework: HomeworkEntity | undefined;
    allHomework: (HomeworkEntity | undefined)[] | undefined;
    allExercises: (ExerciseEntity | undefined)[] | undefined;
    sequence: SequenceOptions;
    setSequence: React.Dispatch<React.SetStateAction<SequenceOptions>>;
    displayPriorKnowledge: boolean;
    displayInstruction: boolean;
    displayMain: boolean;
    priorKnowledgeExercise: ExerciseEntity;
    instructionExercise?: ExerciseEntity;
    mainExercise?: ExerciseEntity;
    priorKnowledgeStatus: exerciseStatusType;
    instructionStatus: exerciseStatusType;
    mainExerciseStatus: exerciseStatusType;
    priorKnowledgeWasEmpty: boolean;
    instructionWasEmpty: boolean;
    mainWasEmpty: boolean;
    priorKnowledgeIsFinished: boolean;
    instructionIsFinished: boolean;
    mainIsFinished: boolean;
    progress: number;
    setProgress: React.Dispatch<React.SetStateAction<number>>;
    background: Background;
};

export const HomeworkContext = createContext({} as HomeworkContextTypes);

export default function Homework() {
    const [sequence, setSequence] = useState<SequenceOptions>('terugblik');
    const dispatch = useDispatch();

    const [
        priorKnowledgeStatus,
        setPriorKnowledgeStatus,
    ] = useState<exerciseStatusType>('not-started');

    const [
        instructionStatus,
        setInstructionStatus,
    ] = useState<exerciseStatusType>('not-started');
    const [
        mainExerciseStatus,
        setMainExerciseStatus,
    ] = useState<exerciseStatusType>('not-started');

    const [priorKnowledgeWasEmpty, setPriorKnowledgeWasEmpty] = useState(false);
    const [instructionWasEmpty, setInstructionWasEmpty] = useState(false);
    const [mainWasEmpty, setMainWasEmpty] = useState(false);
    const [
        showHomeworkRequestFinished,
        setShowHomeworkRequestFinished,
    ] = useState(false);

    const { homeworkId, treatmentId } = useParams() as {
        homeworkId: string;
        treatmentId: string;
    };

    useEffect(() => {
        // @ts-ignore
        dispatch(showHomework(homeworkId)).then(() =>
            setShowHomeworkRequestFinished(true)
        );
    }, [homeworkId]);

    const allHomework = useSelector((state: ReduxStore) =>
        state?.homework?.entities.allIds.map(
            (e) => state?.homework?.entities.byId[e]
        )
    );

    const homework = useSelector(
        (state: ReduxStore) => state?.homework?.entities.byId[homeworkId]
    );

    const allExercises:
        | (ExerciseEntity | undefined)[]
        | undefined = useSelector((state) =>
        // @ts-ignore
        getExercisesByHomework(state, homeworkId)
    );

    const priorKnowledgeExercise = allExercises?.find(
        (e) => e?.homework_type === 'voorkennis'
    );

    const instructionExercise = allExercises?.find(
        (e) => e?.homework_type === 'instructie'
    );

    const mainExercise = allExercises?.find(
        (e) => e?.homework_type === 'oefening'
    );

    const user = useSelector(
        (state: ReduxStore) => state?.authentication?.user
    );

    const priorKnowledgeIsFinished =
        typeof user?.exercise_status?.find(
            (e) =>
                e.exercise_id === priorKnowledgeExercise?.id &&
                e.exercise_type === 'App\\Models\\Exercise' &&
                e.finished === 1
        ) !== 'undefined';

    const instructionIsFinished =
        typeof user?.exercise_status?.find(
            (e) =>
                e.exercise_id === instructionExercise?.id &&
                e.exercise_type === 'App\\Models\\Exercise' &&
                e.finished === 1
        ) !== 'undefined';

    const mainIsFinished =
        typeof user?.exercise_status?.find(
            (e) =>
                e.exercise_id === mainExercise?.id &&
                e.exercise_type === 'App\\Models\\Exercise' &&
                e.finished === 1
        ) !== 'undefined';

    const displayPriorKnowledge =
        sequence === 'voorkennis' && priorKnowledgeExercise !== undefined;

    const displayInstruction =
        sequence === 'instructie' && instructionExercise !== undefined;
    const displayMain = sequence === 'oefening' && mainExercise !== undefined;

    useEffect(() => {
        if (!displayPriorKnowledge && sequence === 'voorkennis') {
            setPriorKnowledgeWasEmpty(true);
            setPriorKnowledgeStatus('finished');
        }
    }, [displayInstruction, sequence]);

    useEffect(() => {
        if (!displayInstruction && sequence === 'instructie') {
            setInstructionWasEmpty(true);
            setInstructionStatus('finished');
        }
    }, [displayInstruction, sequence]);

    useEffect(() => {
        if (!displayMain && sequence === 'oefening') {
            setMainWasEmpty(true);
            setMainExerciseStatus('finished');
        }
    }, [displayMain, sequence]);

    const background = useSelector(
        (state: ReduxStore) =>
            state?.treatment?.entities?.byId[treatmentId]?.world || 'city'
    );

    const [progress, setProgress] = useState<number>(0);

    useEffect(() => {
        if (
            priorKnowledgeStatus === 'finished' &&
            !priorKnowledgeIsFinished &&
            !priorKnowledgeWasEmpty
        ) {
            dispatch(
                updateExerciseStatus({
                    exercise_id: priorKnowledgeExercise?.id,
                    exercise_type: 'homework',
                    finished: 1,
                })
            );
        }
    }, [priorKnowledgeStatus]);

    useEffect(() => {
        if (
            instructionStatus === 'finished' &&
            !instructionIsFinished &&
            !instructionWasEmpty
        ) {
            dispatch(
                updateExerciseStatus({
                    exercise_id: instructionExercise?.id,
                    exercise_type: 'homework',
                    finished: 1,
                })
            );
        }
    }, [instructionStatus]);

    useEffect(() => {
        if (
            mainExerciseStatus === 'finished' &&
            !mainIsFinished &&
            !mainWasEmpty
        ) {
            dispatch(
                updateExerciseStatus({
                    exercise_id: mainExercise?.id,
                    exercise_type: 'homework',
                    finished: 1,
                })
            );
        }
    }, [mainExerciseStatus]);

    const history = useHistory();

    useEffect(() => {
        if (showHomeworkRequestFinished) {
            const nextExercise = allHomework
                ?.filter((e) => e?.treatment_id === homework?.treatment_id)
                .find((e) => {
                    if (e && homework) return e.id > homework.id;
                    return null;
                });

            if (
                (!instructionExercise ||
                    (instructionExercise && instructionIsFinished)) && // check if we have a instructionExercise and if we do, if it has been finished
                (!mainExercise || (mainExercise && mainIsFinished)) && // check if we have a MainExercise, and if we do, if it has been finished
                (!instructionExercise && !mainExercise) === false && // check if we don't have any exercises (in that case we cannot skip to next Exercise)
                nextExercise // check if we have a nextExercise (spellingswerk 2)
            ) {
                // if all of those parameters are valid, we can skip to Spellingswerk 2
                history.push(`/${treatmentId}/homework/${nextExercise.id}`);
            }
        }
    }, [showHomeworkRequestFinished]);

    return (
        <HomeworkContext.Provider
            value={{
                homework,
                allHomework,
                allExercises,
                sequence,
                setSequence,
                displayPriorKnowledge,
                displayInstruction,
                displayMain,
                priorKnowledgeExercise,
                instructionExercise,
                mainExercise,
                priorKnowledgeStatus,
                instructionStatus,
                mainExerciseStatus,
                priorKnowledgeWasEmpty,
                priorKnowledgeIsFinished,
                instructionWasEmpty,
                instructionIsFinished,
                mainIsFinished,
                mainWasEmpty,
                progress,
                setProgress,
                background,
            }}
        >
            <Background background={background}>
                <BackButton />
                <Menu />
                <NavigationSequence />
                <AnimatePresence exitBeforeEnter>
                    {displayPriorKnowledge && (
                        <ConditionalExercise
                            exercise={priorKnowledgeExercise}
                            key={1}
                            setStatus={setPriorKnowledgeStatus}
                        />
                    )}
                    {displayInstruction && (
                        <ConditionalExercise
                            exercise={instructionExercise}
                            key={2}
                            setStatus={setInstructionStatus}
                        />
                    )}
                    {displayMain && (
                        <ConditionalExercise
                            exercise={mainExercise}
                            key={3}
                            setStatus={setMainExerciseStatus}
                        />
                    )}
                </AnimatePresence>
            </Background>
        </HomeworkContext.Provider>
    );
}
